import React, {useEffect, useState} from 'react';
import {DetailGrid} from '@/components/GridWidget';
import {WdiCardTitle, wdiCore, WdiModalAppResult, WdiModalProps} from '@/components/WdiControls';
import {Button, Input, message} from 'antd';
import {initFieldDisplayFormater, TableColumnRanderFormats} from '@/components/WdiBus/TableColumnRanderFormats';
import {NsGrid} from '@/components/GridWidget/GridDefProps';
import DataStore from '@/components/GridWidget/DataStore';
import {WdiPanel} from '@/components/WdiControls/WdiPanel';
import {WdiInputNumber} from '@/components/WdiControls/WdiInputNumber';
import {ImportModalApp} from '@/components/WdiControls/WdiImport';
import {ResponseStatus} from '@/utils/request';
import {Enum} from '@/enums/system';
import {GridApi} from '@/services/gridapi';
import {getSchemaColumns, toExcel} from '@/components/WdiControls/WdiExcel';
import {initApprovalRowFieldConfigs, initRowFieldConfigs, replaceColumns} from '../../components/data';
import {Position} from '@/services/basic/position';
import PageControl from '@/components/WdiBus/FormPage';
import ModifyMktPaymentAmountModalApp from '@/pages/mkt/sp/components/ModifyMktPaymentAmount';
import PaymentDetailEditApp from '@/pages/mkt/sp/GeneralPaymentApply/components/detail-edit';

export type DetailForApplyProps = {
  data?: any,
  dataSource: any[],
  onChange?: (dataSource: any) => void;
  onClose?: (result?: WdiModalAppResult) => void;
}

const PaymentMktDetailsForApplyPanel: React.FC<DetailForApplyProps> = (props) => {
  /// Grid初始化配置，GridSettings请求完数据之后再进行加载
  const [isLoaded, setIsLoaded] = useState(false);
  const [gridSettings, setGridSettings] = useState<any>({});
  const [importModalProps, setImportModalProps] = useState<WdiModalProps>({visible: false});
  const {data, dataSource, onChange} = props;

  const onImportClose = () => {
    setImportModalProps({visible: false, title: '', id: null});
  };
  const onImportSave = (uploadResult: any) => {
    if (uploadResult.code == ResponseStatus.SUCCESS) {
      const newDataSource = [...dataSource];
      const result = uploadResult?.data?.data;
      if (result && result.length > 0) {
        newDataSource.forEach(record => {
          const item = result.find((item: any) => item.formSaMktDetailId == record.formSaMktDetailId);
          if (item) {
            record.amountInVat = wdiCore.Number.ToNumber(item?.amountInVat, 2);
            record.amountExVat = wdiCore.Number.ToNumber(item?.amountExVat, 2);
            record.remark = item?.remark;
            record.tax = wdiCore.Number.ToNumber(record.amountInVat - record.amountExVat, 2);
          }
        });
        onChange?.(newDataSource);
      }
    }
    setImportModalProps({visible: false, title: '', id: null});
  };

  useEffect(() => {
    GridApi.searchConfig({id: 'FormSpMktDetailForGeneral', allowModifyColumns: false}).then((response) => {
      const settings: any = Object.assign({}, response.data, {
        actionsAlign: NsGrid.ActionsAlign.None,
        showAggr: true,
        showExpandedRow: true,
      });

      initFieldDisplayFormater(settings.formSchema.allFields);

      settings.formSchema.allFields.amountNotPaid.aggr = TableColumnRanderFormats.sumAggr('amountNotPaid', 2);
      settings.formSchema.allFields.amountInVat.aggr = TableColumnRanderFormats.sumAggr('amountInVat', 2);
      settings.formSchema.allFields.amountExVat.aggr = TableColumnRanderFormats.sumAggr('amountExVat', 2);
      settings.formSchema.allFields.tax.aggr = TableColumnRanderFormats.sumAggr('tax', 2);
      settings.formSchema.allFields.amountPaid.aggr = TableColumnRanderFormats.sumAggr('amountPaid', 2);

      settings.formSchema.allFields.rowNumber.width = 80;
      settings.formSchema.allFields.productId.width = 250;
      settings.formSchema.allFields.ecMktId.width = 200;
      // settings.formSchema.allFields.contentDesc.width = 200;
      settings.formSchema.allFields.amountSa.width = 150;
      settings.formSchema.allFields.amountNotPaid.width = 150;
      settings.formSchema.allFields.amountInVat.width = 150;
      settings.formSchema.allFields.amountExVat.width = 150;
      settings.formSchema.allFields.remark.width = 200;
      setGridSettings(settings);
      setIsLoaded(true);
    });
  }, []);

  const pageGridSettings = Object.assign({}, gridSettings);
  pageGridSettings.antdProps = {
    dataSource,
    initRowFieldConfigs: (record: any) => {
      return initRowFieldConfigs(record);
    },
    pagination: {
      pageSize: 10,
      showSizeChanger: true,
    },
  };

  const editColumn = {
    amountInVat: (_t: any, record: any, index: any) => {
      return <WdiInputNumber value={record.amountInVat} onBlur={(e: any) => {
        const {...newRecord} = record;
        const vatTypeValue = data?.vatType?.vatTypeValue;
        newRecord.amountInVat = wdiCore.Number.ToNumber(e.target.value, 2);
        if (!wdiCore.Object.IsNullOrUndefined(vatTypeValue)) {
          newRecord.amountExVat = wdiCore.Number.ToNumber(newRecord.amountInVat / (1 + vatTypeValue), 2);
          newRecord.tax = wdiCore.Number.ToNumber(newRecord.amountInVat - newRecord.amountExVat, 2);
        }
        onChange?.(DataStore.edit(dataSource, newRecord));
      }}/>;
    },
    amountExVat: (_t: any, record: any, index: any) => {
      return <WdiInputNumber value={record.amountExVat} onBlur={(e: any) => {
        const {...newRecord} = record;
        newRecord.amountExVat = wdiCore.Number.ToNumber(e.target.value, 2);
        if (!wdiCore.Object.IsNullOrUndefined(newRecord.amountInVat)) {
          newRecord.tax = wdiCore.Number.ToNumber(newRecord.amountInVat - newRecord.amountExVat, 2);
        }
        onChange?.(DataStore.edit(dataSource, newRecord));
      }}/>;
    },
    remark: (_t: any, record: any, index: any) => {
      return <Input value={record.remark} onChange={(e: any) => {
        const {...newRecord} = record;
        newRecord.remark = e.target.value;
        onChange?.(DataStore.edit(dataSource, newRecord));
      }}/>;
    },
  };

  return (
    <>
      <WdiPanel
        title={<WdiCardTitle iconType={Enum.IconType.DETAIL_INFO} title='费用明细信息'/>}
        extra={
          <>
            <Button type='primary' onClick={() => {
              const newDataSource = [...dataSource];
              if (data.vatType == null) {
                message.error('请选择税率类型');
                return;
              }
              const vatTypeValue = data.vatType.vatTypeValue;
              newDataSource.forEach(item => {
                item.amountInVat = wdiCore.Number.ToNumber(item.amountNotPaid, 2);
                if (!wdiCore.Object.IsNullOrUndefined(vatTypeValue)) {
                  item.amountExVat = wdiCore.Number.ToNumber(item.amountInVat / (1 + vatTypeValue), 2);
                  item.tax = wdiCore.Number.ToNumber(item.amountInVat - item.amountExVat, 2);
                }
              });
              onChange?.(newDataSource);
              return true;
            }} style={{float: 'right', marginRight: 10}}>
              全部含税报销
            </Button>
            <Button type='primary' onClick={() => {
              const newDataSource = [...dataSource];
              if (data.vatType == null) {
                message.error('请选择税率类型');
                return;
              }
              const vatTypeValue = data.vatType.vatTypeValue;
              newDataSource.forEach(item => {
                item.amountExVat = wdiCore.Number.ToNumber(item.amountNotPaid, 2);
                if (!wdiCore.Object.IsNullOrUndefined(vatTypeValue)) {
                  item.amountInVat = wdiCore.Number.ToNumber(item.amountExVat * (1 + vatTypeValue), 2)
                  item.tax = wdiCore.Number.ToNumber(item.amountInVat - item.amountExVat, 2);
                }
              });
              onChange?.(newDataSource);
              return true;
            }} style={{float: 'right', marginRight: 10}}>
              全部未税报销
            </Button>
            <Button hidden={true} type='primary' onClick={() => {
              setImportModalProps({title: '核销信息导入', visible: true});
              return true;
            }}
                    style={{float: 'right', marginRight: 10}}>
              核销信息导入
            </Button>
          </>
        }
      >
        {
          isLoaded &&
          importModalProps.visible &&
          <ImportModalApp
            title={importModalProps.title}
            visible={importModalProps.visible}
            settingsUrl='/expenseapi/spMktButtons/getImportSettings'
            uploadingUrl='/expenseapi/spMktButtons/importUploading'
            onClose={onImportClose}
            onSave={onImportSave}
            clientParameters={{
              pageTypeId: data.pageTypeId,
              details: JSON.stringify(dataSource),
            }}
            events={{
              downloadFormSpMktDetailForApply: () => {
                const columns = [
                  {name: 'rowNumber', displayName: '行号'},
                  {
                    name: 'tpmDetailId',
                    displayName: '活动明细编号*'
                  },
                  {name: 'productCode', displayName: '产品别编号'},
                  {name: 'productName', displayName: '产品别名称'},
                  {name: 'ecMktName', displayName: '费用项'},
                  {name: 'paymentMethodName', displayName: '支付方式'},
                  {name: 'amountSa', displayName: '申请金额', type: Enum.FieldType.NUMBER},
                  {name: 'amountNotPaid', displayName: '可核销金额', type: Enum.FieldType.NUMBER},
                  {name: 'amountInVat', displayName: '核销金额(含税)*(用户填写)', type: Enum.FieldType.NUMBER},
                  {name: 'amountEXVat', displayName: '核销金额(未税)*(用户填写)', type: Enum.FieldType.NUMBER},
                  {name: 'remark', displayName: '核销备注(用户填写)'},
                  {name: 'amountPaid', displayName: '已核销金额', type: Enum.FieldType.NUMBER},
                  {name: 'remarkSa', displayName: '申请备注'},
                ];
                const sheet = {
                  data: dataSource,
                  names: columns,
                  sheetName: 'FormSpMktApply',
                };
                toExcel({
                  name: '核销信息_' + wdiCore.Date.ToString(new Date()) + '.xlsx',
                  sheetProps: [sheet],
                });
              },
            }}
          />
        }
        {isLoaded && <DetailGrid {...pageGridSettings} params={data} editColumn={editColumn}/>}
      </WdiPanel>
    </>
  );
};
export default PaymentMktDetailsForApplyPanel;

const ModalAppTypeEnums = {
  None: 0,
  Update: 1,
  Import: 2,
};
export const PaymentMktDetailsForApprovalPanel: React.FC<DetailForApplyProps> = (props) => {

  /// Grid初始化配置，GridSettings请求完数据之后再进行加载
  const [isLoaded, setIsLoaded] = useState(false);
  const [gridSettings, setGridSettings] = useState<any>({});
  const [modalProps, setModalProps] = useState<WdiModalProps>({visible: false, type: ModalAppTypeEnums.None});
  const {data, dataSource, onChange} = props;

  useEffect(() => {
    if (!data.formSpMkt || !dataSource || dataSource.length == 0) return;
    Promise.all([GridApi.searchConfig({id: 'FormSpMktDetailForApproval', allowModifyColumns: true}),
      Position.access({useCaseId: Enum.UseCase.SP_MKT, operateTypeIds: [Enum.OperateType.MODIFY_AMOUNT]}),
    ]).then((dataArray: any[]) => {
      const responseResult = dataArray[0];
      const settings: any = Object.assign({}, responseResult.data, {
        actionsAlign: NsGrid.ActionsAlign.Left,
        showAggr: true,
        showExpandedRow: true,
      });

      initFieldDisplayFormater(settings.formSchema.allFields);

      settings.formSchema.allFields.amountNotPaid.aggr = TableColumnRanderFormats.sumAggr('amountNotPaid', 2);
      settings.formSchema.allFields.amountInVat.aggr = TableColumnRanderFormats.sumAggr('amountInVat', 2);
      settings.formSchema.allFields.amountExVat.aggr = TableColumnRanderFormats.sumAggr('amountExVat', 2);
      settings.formSchema.allFields.tax.aggr = TableColumnRanderFormats.sumAggr('tax', 2);
      settings.formSchema.allFields.amountPaid.aggr = TableColumnRanderFormats.sumAggr('amountPaid', 2);


      settings.formSchema.actions = [];

      settings.formSchema.allFields.rowNumber.width = 80;
      settings.formSchema.allFields.productId.width = 250;
      settings.formSchema.allFields.ecMktId.width = 200;
      // settings.formSchema.allFields.contentDesc.width = 200;
      settings.formSchema.allFields.amountSa.width = 150;
      settings.formSchema.allFields.amountNotPaid.width = 150;
      settings.formSchema.allFields.amountInVat.width = 150;
      settings.formSchema.allFields.amountExVat.width = 150;
      settings.formSchema.allFields.remark.width = 200;

      setGridSettings(settings);
      setIsLoaded(true);
    });
  }, [data, dataSource]);


  const pageGridSettings = Object.assign({}, gridSettings);
  pageGridSettings.antdProps = {
    dataSource,
    initRowFieldConfigs: (record: any) => {
      return initApprovalRowFieldConfigs(record);
    },
    pagination: {
      defaultPageSize: 10,
      showSizeChanger: true,
    },
  };

  const onClose = () => {
    setModalProps({visible: false, type: ModalAppTypeEnums.None});
  };

  const onImportSave = () => {
    onClose();
    props.onClose?.();
    PageControl.approval({
      formTypeId: data.formSpMkt?.formTypeId,
      pageTypeId: data.formSpMkt?.pageTypeId,
      data: {formId: data.formSpMkt?.formSpMktId},
    });
  };

  return (
    <>
      {
        modalProps.visible &&
        modalProps.type == ModalAppTypeEnums.Update &&
        <PaymentDetailEditApp
          {...modalProps}
          onSave={(result) => {
            onChange?.(DataStore.edit(dataSource, result?.data));
            setModalProps({visible: false, data: null});
          }}
          onClose={onClose}
        />
      }
      {
        modalProps.visible && modalProps.type == ModalAppTypeEnums.Import &&
        <ModifyMktPaymentAmountModalApp
          {...modalProps}
          onClose={onClose}
          onSave={onImportSave}
        />
      }
      <WdiPanel
        title={<WdiCardTitle iconType={Enum.IconType.DETAIL_INFO} title='费用明细信息'/>}
        collapsed={true}
        extra={
          <>
            <Button type='primary' onClick={(e) => {
              getSchemaColumns('FormSpMktDetailForApproval', replaceColumns).then(columns => {
                const sheet = {
                  data: dataSource,
                  names: columns,
                  cells: {
                    payToUserId: (fieldObject: any, row: any, value: any) => {
                      return wdiCore.String.GenerateLabelDescription(
                        wdiCore.String.CodeOrName(row.payToUserNo, row.payToUserName),
                        row.payToUserTitle
                      );
                    },
                  },
                  sheetName: 'FormSPMktApply',
                };
                toExcel({
                  name: data?.form?.formNo + '_' + wdiCore.Date.ToString(new Date()) + '.xlsx',
                  sheetProps: [sheet],
                });
              });
              e.stopPropagation();
            }} style={{float: 'right', marginRight: 10}}>
              导出
            </Button>
          </>
        }
      >
        {isLoaded && <DetailGrid {...pageGridSettings} />}
      </WdiPanel>
    </>
  );
};
